import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import loadable from '@loadable/component'
import type AdyenCheckoutType from '@adyen/adyen-web'
import type DropinElement from '@adyen/adyen-web/dist/types/components/Dropin'
import type { CoreOptions } from '@adyen/adyen-web/dist/types/core/types'
import type { PaymentAction, PaymentMethod } from '@adyen/adyen-web/dist/types/types'

// Actions
import { actions as payActions } from 'modules/pay/actions'

// Components
import { Toast } from '@tracklib/kit'

// Hooks
import { useRequest } from 'modules/requests/hooks'

// Types
import { Dispatch } from 'types'

// Utils
import { ADYEN_CLIENT_KEY, ADYEN_ENVIRONMENT } from '@constants'

import styles from './newcardform.css'

const AdyenCheckoutLoadable = loadable.lib(() => import('@adyen/adyen-web'))
interface PayPalFormProps {
  paymentMethods: Array<PaymentMethod>
  onSuccess?: () => void
}

const PayPalForm = ({ paymentMethods, onSuccess }: PayPalFormProps) => {
  const dispatch = useDispatch() as Dispatch
  const [errorText, setErrorText] = useState('')
  const addExternalPaymentMethodRequest = useRequest('ADD_EXTERNAL_PAYMENT_METHOD')
  const saveExternalPaymentMethodRequest = useRequest('SAVE_EXTERNAL_PAYMENT_METHOD')
  useEffect(() => {
    const adyenLoadPromise = AdyenCheckoutLoadable.load() as unknown as Promise<{ default: typeof AdyenCheckoutType }>
    adyenLoadPromise.then(({ default: AdyenCheckout }) => {
      const configuration: CoreOptions = {
        paymentMethodsResponse: { paymentMethods },
        clientKey: ADYEN_CLIENT_KEY,
        environment: ADYEN_ENVIRONMENT,
        locale: 'en-US',
        paypal: {
          onCancel: (_: undefined, dropin: DropinElement) => {
            dropin.setStatus('ready')
          },
        },
        onSubmit: ({ data: { paymentMethod } }: { data: { paymentMethod: PaymentMethod } }, dropin: DropinElement) => {
          dispatch<{ data: { response: { action: PaymentAction } } }>(payActions.addExternalPaymentMethod(paymentMethod)).then(response => {
            if (response?.data?.response?.action) {
              dropin.handleAction(response.data.response.action)
            }
          })
        },
        onAdditionalDetails: ({ data }: { data: Record<string, unknown> }) => {
          dispatch<{ data: { error: string } }>(payActions.saveExternalPaymentMethod(data)).then(({ data: { error } }) => {
            if (!error) {
              if (onSuccess) {
                onSuccess()
              }
            } else {
              setErrorText(error)
            }
          })
        },
      }
      const checkout = new AdyenCheckout(configuration)
      checkout.create('dropin').mount('#dropin-container-paypal')
    })
  }, []) // eslint-disable-line
  return (
    <>
      {((addExternalPaymentMethodRequest.hasError && !addExternalPaymentMethodRequest.isLoading) ||
        (saveExternalPaymentMethodRequest.hasError && !saveExternalPaymentMethodRequest.isLoading)) && (
        <Toast className={styles.error} description={`${errorText || 'Something went wrong.'} Please try again.`} />
      )}
      <div id="dropin-container-paypal" />
    </>
  )
}

export default PayPalForm
